import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  CheckSquare, Plus, Search, Calendar, Flag, User, Trash2,
  Edit2, X, Check, Clock, AlertCircle, Loader2, ListTodo,
  ChevronDown, Filter
} from "lucide-react";

// Priority configuration
const PRIORITIES = {
  high: { label: "High", color: "bg-red-100 text-red-700 border-red-200", dotColor: "bg-red-500" },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700 border-amber-200", dotColor: "bg-amber-500" },
  low: { label: "Low", color: "bg-blue-100 text-blue-700 border-blue-200", dotColor: "bg-blue-500" }
};

// Status configuration
const STATUSES = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-700" },
  done: { label: "Done", color: "bg-emerald-100 text-emerald-700" }
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Check if task is overdue
const isOverdue = (dueDate, status) => {
  if (!dueDate || status === "done") return false;
  const due = new Date(dueDate);
  due.setHours(23, 59, 59, 999);
  return due < new Date();
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const config = PRIORITIES[priority] || PRIORITIES.medium;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUSES[status] || STATUSES.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Task Card Component
const TaskCard = ({ task, onToggleDone, onEdit, onDelete }) => {
  const overdue = isOverdue(task.due_date, task.status);
  const isDone = task.status === "done";
  
  return (
    <div 
      className={`p-4 bg-white rounded-lg border transition-all hover:shadow-md ${
        overdue ? "border-red-300 bg-red-50/50" : "border-slate-200"
      } ${isDone ? "opacity-60" : ""}`}
      data-testid={`task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={() => onToggleDone(task)}
          className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            isDone 
              ? "bg-emerald-500 border-emerald-500 text-white" 
              : "border-slate-300 hover:border-emerald-400"
          }`}
          data-testid={`task-checkbox-${task.id}`}
        >
          {isDone && <Check className="w-3 h-3" />}
        </button>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className={`font-medium text-[#1e3a5f] ${isDone ? "line-through text-slate-400" : ""}`}>
              {task.title}
            </h3>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => onEdit(task)}
                className="p-1.5 text-slate-400 hover:text-[#1e3a5f] hover:bg-slate-100 rounded"
                data-testid={`task-edit-${task.id}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(task)}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                data-testid={`task-delete-${task.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {task.description && (
            <p className={`text-sm text-slate-500 mb-2 ${isDone ? "line-through" : ""}`}>
              {task.description}
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <PriorityBadge priority={task.priority} />
            
            {task.due_date && (
              <span className={`flex items-center gap-1 ${
                overdue ? "text-red-600 font-medium" : "text-slate-500"
              }`}>
                {overdue ? <AlertCircle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                {formatDate(task.due_date)}
                {overdue && " (Overdue)"}
              </span>
            )}
            
            {task.client_name && (
              <span className="flex items-center gap-1 text-slate-500">
                <User className="w-3 h-3" />
                {task.client_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Task Form Modal
const TaskFormModal = ({ task, clients, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: task?.title || "",
    description: task?.description || "",
    due_date: task?.due_date || "",
    priority: task?.priority || "medium",
    client_id: task?.client_id || "",
    status: task?.status || "pending"
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        client_id: formData.client_id || null
      });
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="task-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1e3a5f]">
            {task ? "Edit Task" : "New Task"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title..."
              className="border-slate-200"
              data-testid="task-title-input"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details..."
              className="border-slate-200 resize-none"
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          
          {/* Due Date & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="border-slate-200"
                data-testid="task-due-date-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                data-testid="task-priority-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
              data-testid="task-status-select"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>
          
          {/* Related Client */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Related Client</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
              data-testid="task-client-select"
            >
              <option value="">None</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.full_name}</option>
              ))}
            </select>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              data-testid="task-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {task ? "Update" : "Create"} Task
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Empty State
const EmptyState = ({ onAddTask }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
      <ListTodo className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No tasks yet</h3>
    <p className="text-slate-500 mb-4">Add your first task to stay organized.</p>
    <Button onClick={onAddTask} className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]">
      <Plus className="w-4 h-4 mr-2" />
      Add First Task
    </Button>
  </div>
);

// Main Component
export default function ProTasksPage() {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("due_date");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("sort", sortBy);
      
      const response = await axios.get(`${API}/pro/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery, sortBy]);

  // Fetch clients for dropdown
  const fetchClients = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      // Silently fail - clients dropdown will be empty
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
    fetchClients();
  }, [fetchTasks, fetchClients]);

  // Create task
  const handleCreateTask = async (taskData) => {
    try {
      await axios.post(`${API}/pro/tasks`, taskData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Task created");
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create task");
      throw error;
    }
  };

  // Update task
  const handleUpdateTask = async (taskData) => {
    if (!editingTask) return;
    
    try {
      await axios.put(`${API}/pro/tasks/${editingTask.id}`, taskData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Task updated");
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update task");
      throw error;
    }
  };

  // Toggle done status
  const handleToggleDone = async (task) => {
    const newStatus = task.status === "done" ? "pending" : "done";
    
    try {
      await axios.put(`${API}/pro/tasks/${task.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Optimistic update
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
      
      toast.success(newStatus === "done" ? "Task completed!" : "Task reopened");
    } catch (error) {
      toast.error("Failed to update task");
      fetchTasks(); // Revert on error
    }
  };

  // Delete task
  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/pro/tasks/${task.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Open edit modal
  const handleEdit = (task) => {
    setEditingTask(task);
    setShowModal(true);
  };

  // Open create modal
  const handleAddNew = () => {
    setEditingTask(null);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  // Group tasks by status for display
  const groupedTasks = {
    pending: tasks.filter(t => t.status === "pending"),
    in_progress: tasks.filter(t => t.status === "in_progress"),
    done: tasks.filter(t => t.status === "done")
  };

  // Count stats
  const stats = {
    total: tasks.length,
    pending: groupedTasks.pending.length,
    in_progress: groupedTasks.in_progress.length,
    done: groupedTasks.done.length,
    overdue: tasks.filter(t => isOverdue(t.due_date, t.status)).length
  };

  return (
    <div className="space-y-6" data-testid="pro-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Tasks</h1>
          <p className="text-slate-500">Manage your work and stay organized</p>
        </div>
        
        <Button
          onClick={handleAddNew}
          className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]"
          data-testid="add-task-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#1e3a5f]">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Tasks</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.in_progress}</p>
            <p className="text-xs text-slate-500">In Progress</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${stats.overdue > 0 ? "bg-red-50 border-red-200" : ""}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${stats.overdue > 0 ? "text-red-600" : "text-emerald-600"}`}>
              {stats.overdue > 0 ? stats.overdue : stats.done}
            </p>
            <p className="text-xs text-slate-500">{stats.overdue > 0 ? "Overdue" : "Completed"}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-slate-200"
                data-testid="tasks-search"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                data-testid="tasks-status-filter"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            
            {/* Sort */}
            <div className="flex items-center gap-2">
              <ChevronDown className="w-4 h-4 text-slate-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                data-testid="tasks-sort"
              >
                <option value="due_date">Due Date</option>
                <option value="priority">Priority</option>
                <option value="created_at">Created Date</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Task List */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
        </div>
      ) : tasks.length === 0 && !searchQuery && statusFilter === "all" ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <EmptyState onAddTask={handleAddNew} />
          </CardContent>
        </Card>
      ) : tasks.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">No tasks match your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Show all tasks when filtering by status or searching */}
          {(statusFilter !== "all" || searchQuery) ? (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#1e3a5f]">
                  {statusFilter !== "all" ? STATUSES[statusFilter]?.label : "Search Results"} ({tasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onToggleDone={handleToggleDone}
                    onEdit={handleEdit}
                    onDelete={handleDeleteTask}
                  />
                ))}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Pending Tasks */}
              {groupedTasks.pending.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Pending ({groupedTasks.pending.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedTasks.pending.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onToggleDone={handleToggleDone}
                        onEdit={handleEdit}
                        onDelete={handleDeleteTask}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
              
              {/* In Progress Tasks */}
              {groupedTasks.in_progress.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
                      <Loader2 className="w-5 h-5 text-purple-500" />
                      In Progress ({groupedTasks.in_progress.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedTasks.in_progress.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onToggleDone={handleToggleDone}
                        onEdit={handleEdit}
                        onDelete={handleDeleteTask}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
              
              {/* Completed Tasks */}
              {groupedTasks.done.length > 0 && (
                <Card className="border-slate-200 bg-slate-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-slate-500 flex items-center gap-2">
                      <Check className="w-5 h-5 text-emerald-500" />
                      Completed ({groupedTasks.done.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {groupedTasks.done.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onToggleDone={handleToggleDone}
                        onEdit={handleEdit}
                        onDelete={handleDeleteTask}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Task Form Modal */}
      {showModal && (
        <TaskFormModal
          task={editingTask}
          clients={clients}
          onSave={editingTask ? handleUpdateTask : handleCreateTask}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
